import React, { useState, useEffect } from 'react';
import {
  EyeIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  GlobeAltIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { 
  EyeIcon as EyeSolidIcon,
  BuildingOfficeIcon as BuildingSolidIcon
} from '@heroicons/react/24/solid';

interface ProfileView {
  id: string;
  viewerId: string;
  viewerType: 'employer' | 'student';
  viewerProfile: {
    id: string;
    companyName?: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
  };
  profileOwnerId: string;
  viewedAt: Date;
  sessionDuration?: number;
  pagesViewed?: string[];
  source?: string;
}

interface ProfileViewStats {
  totalViews: number;
  uniqueViewers: number;
  viewsThisWeek: number;
  viewsThisMonth: number;
  topViewerCompanies: {
    companyName: string;
    companyLogo?: string;
    viewCount: number;
    lastViewed: Date;
  }[];
  recentViews: ProfileView[];
  viewsBySource: {
    [source: string]: number;
  };
}

interface Props {
  isPremium: boolean;
}

const ProfileViewsInsights: React.FC<Props> = ({ isPremium }) => {
  const [stats, setStats] = useState<ProfileViewStats | null>(null);
  const [recentViews, setRecentViews] = useState<ProfileView[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'recent' | 'companies'>('overview');

  useEffect(() => {
    if (isPremium) {
      fetchProfileViewStats();
      fetchRecentViews();
    }
    setLoading(false);
  }, [isPremium]);

  const fetchProfileViewStats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/profile-views/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setStats(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching profile view stats:', error);
    }
  };

  const fetchRecentViews = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/profile-views/recent`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setRecentViews(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching recent views:', error);
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'search':
        return <GlobeAltIcon className="h-4 w-4" />;
      case 'job_application':
        return <BuildingOfficeIcon className="h-4 w-4" />;
      case 'direct_link':
        return <EyeIcon className="h-4 w-4" />;
      default:
        return <EyeIcon className="h-4 w-4" />;
    }
  };

  if (!isPremium) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full">
              <EyeSolidIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Profile View Insights
          </h3>
          <p className="text-gray-600 mb-4">
            See which employers are checking out your profile and track your visibility
          </p>
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-center mb-2">
              <StarIcon className="h-5 w-5 text-yellow-500 mr-2" />
              <span className="font-medium text-gray-900">Premium Feature</span>
            </div>
            <p className="text-sm text-gray-600">
              Upgrade to premium to unlock profile view insights and see exactly who's interested in your profile.
            </p>
          </div>
          <button 
            onClick={() => window.location.href = '/subscription'}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-2 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-colors"
          >
            Upgrade to Premium
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-300 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <EyeSolidIcon className="h-6 w-6 text-indigo-600 mr-2" />
            <h2 className="text-xl font-bold text-gray-900">Profile View Insights</h2>
          </div>
          <div className="flex items-center space-x-1 bg-gradient-to-r from-indigo-100 to-purple-100 px-3 py-1 rounded-full">
            <StarIcon className="h-4 w-4 text-yellow-500" />
            <span className="text-sm font-medium text-gray-700">Premium</span>
          </div>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center">
                <EyeIcon className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-blue-600">Total Views</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.totalViews}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center">
                <UserGroupIcon className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-green-600">Unique Viewers</p>
                  <p className="text-2xl font-bold text-green-900">{stats.uniqueViewers}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center">
                <CalendarDaysIcon className="h-8 w-8 text-purple-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-purple-600">This Week</p>
                  <p className="text-2xl font-bold text-purple-900">{stats.viewsThisWeek}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center">
                <ArrowTrendingUpIcon className="h-8 w-8 text-orange-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-orange-600">This Month</p>
                  <p className="text-2xl font-bold text-orange-900">{stats.viewsThisMonth}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('recent')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'recent'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Recent Views
            </button>
            <button
              onClick={() => setActiveTab('companies')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'companies'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Top Companies
            </button>
          </nav>
        </div>

        <div className="mt-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && stats && (
            <div className="space-y-6">
              {/* Views by Source */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Views by Source</h3>
                <div className="space-y-2">
                  {Object.entries(stats.viewsBySource).map(([source, count]) => (
                    <div key={source} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        {getSourceIcon(source)}
                        <span className="ml-2 text-sm font-medium text-gray-700 capitalize">
                          {source.replace('_', ' ')}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-gray-900">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Recent Views Tab */}
          {activeTab === 'recent' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Profile Views</h3>
              {recentViews.length > 0 ? (
                <div className="space-y-3">
                  {recentViews.map((view) => (
                    <div key={view.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                          {view.viewerProfile.avatar ? (
                            <img src={view.viewerProfile.avatar} alt="" className="h-10 w-10 rounded-full" />
                          ) : (
                            <BuildingSolidIcon className="h-5 w-5 text-indigo-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {view.viewerProfile.companyName || 
                             `${view.viewerProfile.firstName} ${view.viewerProfile.lastName}` ||
                             'Unknown Viewer'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {view.source && (
                              <span className="capitalize">{view.source.replace('_', ' ')}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">{formatDate(view.viewedAt)}</p>
                        {view.sessionDuration && (
                          <p className="text-xs text-gray-400">
                            {Math.round(view.sessionDuration / 60)}min view
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <EyeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No profile views yet</p>
                </div>
              )}
            </div>
          )}

          {/* Top Companies Tab */}
          {activeTab === 'companies' && stats && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top Viewing Companies</h3>
              {stats.topViewerCompanies.length > 0 ? (
                <div className="space-y-3">
                  {stats.topViewerCompanies.map((company, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <div className="h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                          {company.companyLogo ? (
                            <img src={company.companyLogo} alt="" className="h-12 w-12 rounded-full" />
                          ) : (
                            <BuildingSolidIcon className="h-6 w-6 text-indigo-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{company.companyName}</p>
                          <p className="text-xs text-gray-500">
                            Last viewed: {formatDate(company.lastViewed)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-indigo-600">{company.viewCount}</p>
                        <p className="text-xs text-gray-500">views</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No company views yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileViewsInsights;